import { useMemo } from 'react';

import {
  Configuration01Icon,
  InformationCircleIcon,
  Message01Icon,
  MessageBlockedIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { InputGroup, ParamTextarea, Toggle, ToggleLabel } from 'components/FormComponents';

import { SmsPreview } from './SmsPreview';

const SMS_COPY_MAX_LENGTH = 160;

export const Sms = ({
  register,
  readOnly = false,
  backgroundColor = 'background-secondary',
  brand,
  control,
}) => {
  const { t } = useTranslation();
  const smsCommunicationValue = useWatch({ control, name: 'use_sms_communication' });
  const smsActiveValue = useWatch({ control, name: 'sms_active' });
  const smsCandidateActiveValue = useWatch({ control, name: 'sms_candidate_active' });
  const smsRefereeActiveValue = useWatch({ control, name: 'sms_referee_active' });
  const smsCandidateText = useWatch({ control, name: 'sms_candidate_copy' });
  const smsRefereeText = useWatch({ control, name: 'sms_referee_copy' });

  const smsCandidateTextParams = useMemo(
    () => ({
      candidateFirstName: '{{candidateFirstName}}',
      candidateFullName: '{{candidateFullName}}',
      candidateLastName: '{{candidateLastName}}',
      user: '{{user}}',
      company: '{{company}}',
    }),
    [],
  );

  const smsRefereeTextParams = useMemo(
    () => ({
      refereeFirstName: '{{refereeFirstName}}',
      refereeFullName: '{{refereeFullName}}',
      refereeLastName: '{{refereeLastName}}',
      candidateFirstName: '{{candidateFirstName}}',
      candidateFullName: '{{candidateFullName}}',
      candidateLastName: '{{candidateLastName}}',
      company: '{{company}}',
    }),
    [],
  );

  const smsCandidateCopyValidators = useMemo(
    () => ({
      validate: value => {
        if (!smsCandidateActiveValue) return true;
        const text = value != null ? String(value) : '';
        if (text.trim() === '') {
          return 'You have enabled the SMS candidate template, please add a template to continue.';
        }
        if (text.length > SMS_COPY_MAX_LENGTH) {
          return t('company.tabs.branding.emails.smsMaxLength', { max: SMS_COPY_MAX_LENGTH });
        }
        return true;
      },
    }),
    [smsCandidateActiveValue, t],
  );

  const smsRefereeCopyValidators = useMemo(
    () => ({
      validate: value => {
        if (!smsRefereeActiveValue) return true;
        if (value != null && String(value).trim() !== '') return true;
        return 'You have enabled the SMS referee template, please add a template to continue.';
      },
    }),
    [smsRefereeActiveValue],
  );

  const smsPreview = useMemo(() => {
    return {
      candidateText:
        smsActiveValue && smsCandidateActiveValue && smsCandidateText
          ? smsCandidateText
          : 'Hi, {{candidate}}, {{user}} at {{company}} has requested you submit a check using Checkmate. Follow the link to complete it.',
      refereeText:
        smsActiveValue && smsRefereeActiveValue && smsRefereeText
          ? smsRefereeText
          : 'Hi, {{referee}}, {{candidate}} nominated you as an employment reference for a role with {{company}}. Follow the link to complete it.',
    };
  }, [smsCandidateText, smsRefereeText, smsCandidateActiveValue, smsRefereeActiveValue]);

  return (
    <BrandingTabSplitLayout
      key={brand?.id}
      shellClassName={backgroundColor}
      preview={smsCommunicationValue ? <SmsPreview {...smsPreview} /> : null}>
      <div className="flex flex-col gap-8">
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex justify-between items-start">
          <div className="flex gap-2 flex-grow-1 w-3/4">
            <HugeiconsIcon icon={Message01Icon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2">
              <span className="text-2xl font-medium text-neutral-7">SMS Communication</span>
              <p className="text-xl text-text-tertiary">
                {t('company.tabs.branding.emails.useSmsCommunicationDescription')}
              </p>
            </div>
          </div>
          <div className="w-auto flex-shrink-0">
            <InputGroup noMargin>
              <Toggle register={register} name="use_sms_communication" small />
            </InputGroup>
          </div>
        </section>
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex flex-col gap-8">
          {smsCommunicationValue ? (
            <>
              <div className="flex justify-between items-start">
                <div className="flex gap-2 flex-grow-1 w-3/4">
                  <HugeiconsIcon
                    icon={Configuration01Icon}
                    size="24"
                    className="shrink-0 text-neutral-6"
                  />
                  <div className="flex flex-col gap-2">
                    <span className="text-2xl font-medium text-neutral-7">Customisation</span>
                    <p className="text-xl text-text-tertiary">
                      Customise SMS templates for candidates and referees.
                    </p>
                  </div>
                </div>
                <div className="w-auto flex-shrink-0">
                  <InputGroup noMargin>
                    <Toggle register={register} name="sms_active" small />
                  </InputGroup>
                </div>
              </div>
              {smsActiveValue ? (
                <div className="flex flex-col gap-8 w-full border-t border-neutral-4 pt-8">
                  <div className="flex flex-col gap-4">
                    <InputGroup noMargin>
                      <ToggleLabel
                        register={register}
                        name="sms_candidate_active"
                        label="Candidate SMS template"
                      />
                    </InputGroup>
                    <InputGroup
                      noMargin
                      disabled={!smsCandidateActiveValue}
                      description={t('company.tabs.branding.emails.smsMaxLength', {
                        max: SMS_COPY_MAX_LENGTH,
                      })}>
                      <ParamTextarea
                        control={control}
                        name="sms_candidate_copy"
                        textParams={smsCandidateTextParams}
                        placeholder={t('company.tabs.branding.emails.smsCandidateBodyPlaceholder')}
                        rows={10}
                        maxLength={SMS_COPY_MAX_LENGTH}
                        validators={smsCandidateCopyValidators}
                      />
                    </InputGroup>
                  </div>
                  <div className="flex flex-col gap-4">
                    <InputGroup noMargin>
                      <ToggleLabel
                        register={register}
                        name="sms_referee_active"
                        label="Referee SMS template"
                      />
                    </InputGroup>
                    <InputGroup
                      noMargin
                      disabled={!smsRefereeActiveValue}
                      description={t('company.tabs.branding.emails.smsMaxLength', {
                        max: SMS_COPY_MAX_LENGTH,
                      })}>
                      <ParamTextarea
                        control={control}
                        name="sms_referee_copy"
                        textParams={smsRefereeTextParams}
                        placeholder={t('company.tabs.branding.emails.smsRefereeBodyPlaceholder')}
                        rows={10}
                        maxLength={SMS_COPY_MAX_LENGTH}
                        validators={smsRefereeCopyValidators}
                      />
                    </InputGroup>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 items-center justify-center w-full border-t border-neutral-4 pt-8">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    size="20"
                    strokeWidth="1.5"
                    className="text-neutral-6"
                  />
                  <p className="text-xl text-text-tertiary">
                    Using default templates. Toggle on to customise.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-8">
              <div className="flex gap-2 w-full">
                <HugeiconsIcon
                  icon={Configuration01Icon}
                  size="24"
                  className="shrink-0 text-neutral-6"
                />
                <div className="flex flex-col gap-2">
                  <span className="text-2xl font-medium text-neutral-7">Customisation</span>
                  <p className="text-xl text-text-tertiary">
                    Customise SMS templates for candidates and referees.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-4 w-full border-t border-neutral-4 pt-8 items-center justify-center">
                <HugeiconsIcon
                  icon={MessageBlockedIcon}
                  size="80"
                  strokeWidth="1.5"
                  className="text-neutral-6"
                />
                <div className="flex gap-2 items-center justify-center w-full">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    size="20"
                    strokeWidth="1.5"
                    className="text-neutral-6"
                  />
                  <p className="text-xl text-text-tertiary">
                    SMS communication is disabled. Please enable it to customise SMS templates.
                  </p>
                </div>
              </div>{' '}
            </div>
          )}
        </section>
      </div>
    </BrandingTabSplitLayout>
  );
};
